import { FC, useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { axiosHiringApi } from 'src/api/axios'
import { CommunityApi } from 'src/api/community'
import {
  BreadcrumbDivider,
  BreadcrumbHeading,
  Button,
  PageTitle,
  PageTitleSecondary,
  Spinner
} from 'src/components'
import { useBehaviorMapper, useDebouncedLoading } from 'src/hooks'
import { IconProfile } from 'src/icons'
import { EmptyCommunitiesImg } from 'src/images'
import { ICommunityModel } from 'src/interfaces'
import { Breadcrumbs } from 'src/layouts/breadcrumbs'
import { browserHistory, ERoutes, generate } from 'src/router'
import { LoadMoreService } from 'src/services'
import { AuthModule } from 'src/store'
import { CommunityCard } from '../community-card'
import Style from './style.module.scss'

const View: FC = () => {
  const profile = useBehaviorMapper(AuthModule.profile$)

  const _loadMoreService = useMemo(() => new LoadMoreService<ICommunityModel>({
    axiosInstance: axiosHiringApi,
    endpoint: CommunityApi._prefix + '/joined'
  }), [])

  const { ref: refLoadMore, inView } = useInView({ threshold: 0 })
  const loading = useBehaviorMapper(_loadMoreService.loading$)
  const items = useBehaviorMapper<ICommunityModel[]>(_loadMoreService.items$)
  const pagination = useBehaviorMapper(_loadMoreService.pagination$)
  const debounceLoading = useDebouncedLoading(!items.length && loading)

  useEffect(() => {
    if (inView && !loading && _loadMoreService.hasMore) {
      _loadMoreService.loadMore()
    }
  }, [_loadMoreService, inView, loading])

  useEffect(() => {
    if (profile.id) {
      _loadMoreService.loadMore()
    }
  }, [_loadMoreService, profile.id])

  if (debounceLoading) {
    return (
      <div className="fx-1 fx fx-center">
        <Spinner/>
      </div>
    )
  }

  return (
    <div className="fx-1 fx-column gap-8 sm-gap-4 p-8 sm-px-6 sm-py-4">
      <div className="fx fx-ai-center gap-2">
        <PageTitle>My Communities</PageTitle>
        <PageTitleSecondary>({pagination.total})</PageTitleSecondary>
      </div>
      <div className="fx gap-4 fx-wrap-wrap">
        {!!items.length && (
          <>
            {items.map((community) => (
              <CommunityCard
                key={community.id}
                community={community}
                className={Style.communityCard}
                refresh={() => {
                  _loadMoreService.refresh()
                }}
              />
            ))}
            <div ref={refLoadMore}/>
          </>
        )}
        {!items.length && (
          <EmptyCommunities/>
        )}
      </div>
    </div>
  )
}

export const Communities = () => {
  return (
    <>
      <Breadcrumbs>
        <div className="fx fx-ai-center gap-3">
          <BreadcrumbHeading>My Profile</BreadcrumbHeading>
          <BreadcrumbDivider/>
          <BreadcrumbHeading active>Communities</BreadcrumbHeading>
        </div>
      </Breadcrumbs>

      <View/>
    </>
  )
}

export const EmptyCommunities = () => {
  return (
    <div className="fx-1 fx-column fx-center gap-10 mt-20 mx-auto" style={{ maxWidth: 434 }}>
      <img src={EmptyCommunitiesImg} alt="empty-communities" width={120} height={120}/>
      <div className="fx-column fx-center gap-5">
        <div className="bold-24 txt-center txt-black-01">
          You Haven’t Joined a Community Yet
        </div>
        <div className="fs-14 fw-400 lh-20 txt-center txt-black-02">
          Add your location in your Personal Info to join your state’s community and connect with local Hiring Managers.
        </div>
        <Button
          className="fx fx-ai-center gap-2 px-5 py-3"
          onClick={() => browserHistory.push(generate(ERoutes.SETTINGS))}
        >
          <IconProfile/>
          Go to Personal Info
        </Button>
      </div>
    </div>
  )
}
